import { IoCloseOutline } from '@react-icons/all-files/io5/IoCloseOutline'
import { Flex, IconButton } from '@radix-ui/themes'
import clsx from 'clsx'
import { Dialog } from 'radix-ui'
import React, { useEffect, useRef } from 'react'

import styles from './slideout-panel.module.css'

type Props = {
  open: boolean
  onClose: () => void
  /** Left slot of the header row (e.g. avatar + name) */
  header?: React.ReactNode
  /** Icon buttons rendered to the left of the close button (e.g. prev/next, download) */
  headerActions?: React.ReactNode
  children: React.ReactNode
  width?: number
  zIndex?: number
  className?: string
  headerClassName?: string
  contentClassName?: string
  closeButtonClassName?: string
  closeOnClickOutsideEsc?: boolean
}

/**
 * Generic right-side slideout panel with a close button.
 *
 * Built on Radix's `Dialog` primitive (non-modal) for focus handling and
 * Escape handling, while leaving the rest of the page scrollable and
 * interactive behind the panel. Rendered inline in the React tree (no
 * `Dialog.Portal`) so it inherits CSS tokens from the nearest Radix `<Theme>`
 * ancestor. Uses `position: fixed` so it still overlays the full viewport
 * visually.
 *
 * Calls `onClose` when Escape is pressed or (by default) the user clicks
 * outside the panel. Pass navigation or action buttons via `headerActions`.
 */
export const SlideoutPanel = ({
  open,
  onClose,
  header,
  headerActions,
  children,
  width = 400,
  zIndex = 1000,
  className,
  headerClassName,
  contentClassName,
  closeButtonClassName,
  closeOnClickOutsideEsc = true,
}: Props) => {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Radix's built-in close-focus restoration only targets a `Dialog.Trigger`;
  // we drive `open` externally with no Trigger, so restore focus ourselves.
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
    }
  }, [open])

  const preventDismiss = (event: { preventDefault: () => void }) => {
    if (!closeOnClickOutsideEsc) {
      event.preventDefault()
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => !next && onClose()}
      modal={false}
    >
      <Dialog.Content
        className={clsx(styles.panel, className)}
        style={{ width, maxWidth: '100vw', zIndex }}
        onPointerDownOutside={preventDismiss}
        onEscapeKeyDown={preventDismiss}
        onCloseAutoFocus={event => {
          event.preventDefault()
          previousFocusRef.current?.focus()
        }}
      >
        <Flex
          className={clsx(styles.header, headerClassName)}
          align="center"
          justify="between"
          gap="2"
        >
          <Dialog.Title asChild>
            <div className={styles.headerTitle}>{header}</div>
          </Dialog.Title>
          <Flex align="center" gap="1" flexShrink="0">
            {headerActions}
            <IconButton
              variant="ghost"
              size="1"
              onClick={onClose}
              aria-label="Close"
              className={clsx(styles.icon, closeButtonClassName)}
            >
              <IoCloseOutline size={22} />
            </IconButton>
          </Flex>
        </Flex>
        <div className={clsx(styles.content, contentClassName)}>{children}</div>
      </Dialog.Content>
    </Dialog.Root>
  )
}
